import { HistoryActionType } from '../types';
import type { AppLocale } from './locale';

export type HistoryCopy = {
  snapshot: {
    defaultName: (n: number) => string;
    createdAt: (datetime: string) => string;
    loadAction: string;
  };
  dynamic: {
    allClear: string;
    moveGroupTo: (sheet: string) => string;
    movePouchTo: (sheet: string) => string;
    moveGroupFrom: (sheet: string) => string;
    movePouchFrom: (sheet: string) => string;
    clonePouchFrom: (sheet: string) => string;
    cloneGroupFrom: (sheet: string) => string;
    enableBalancing: string;
    disableBalancing: string;
    changeBalancing: string;
    welcomeContent: string;
  };
};

const ACTION_LABELS: Record<AppLocale, Record<HistoryActionType, string>> = {
  en: {
    [HistoryActionType.INIT]: 'Initial State',
    [HistoryActionType.ADD_POUCH]: 'Add Pouch',
    [HistoryActionType.EDIT_POUCH_VALUE]: 'Edit Pouch Value',
    [HistoryActionType.EDIT_GTP_VALUE]: 'Edit GTP Value',
    [HistoryActionType.EDIT_POUCH_TITLE]: 'Edit Pouch Title',
    [HistoryActionType.EDIT_GROUP_TITLE]: 'Edit Group Title',
    [HistoryActionType.MOVE_POUCH]: 'Move Pouch',
    [HistoryActionType.DELETE_POUCH]: 'Delete Pouch',
    [HistoryActionType.ADD_GROUP]: 'Add Group',
    [HistoryActionType.MOVE_GROUP]: 'Move Group',
    [HistoryActionType.RESIZE_GROUP]: 'Resize Group',
    [HistoryActionType.DELETE_GROUP]: 'Delete Group',
    [HistoryActionType.CHANGE_GROUP_COLOR]: 'Change Group Color',
    [HistoryActionType.MERGE_GROUP]: 'Merge Group',
    [HistoryActionType.MERGE_GTP]: 'Merge Group Total Pouch',
    [HistoryActionType.CHANGE_POUCH_COLOR]: 'Change Pouch Color',
    [HistoryActionType.RESTORE_SNAPSHOT]: 'Restore Snapshot',
    [HistoryActionType.CLONE_POUCH]: 'Clone Pouch',
    [HistoryActionType.CLONE_GROUP]: 'Clone Group',
    [HistoryActionType.MERGE_POUCH]: 'Merge Pouch',
    [HistoryActionType.SPLIT_POUCH]: 'Split Pouch',
    [HistoryActionType.PASTE_BULK_UPDATE]: 'Paste Bulk Update',
    [HistoryActionType.TOGGLE_BALANCING_POUCH]: 'Toggle Balancing Pouch',
    [HistoryActionType.EDIT_POUCH_LAYER_VALUE]: 'Edit Pouch Layer Value',
    [HistoryActionType.ADD_SHEET_LAYER]: 'Add Layer',
    [HistoryActionType.REMOVE_SHEET_LAYER]: 'Delete Layer',
    [HistoryActionType.RENAME_SHEET_LAYER]: 'Edit Layer',
  },
  ja: {
    [HistoryActionType.INIT]: '初期状態',
    [HistoryActionType.ADD_POUCH]: 'パウチを追加',
    [HistoryActionType.EDIT_POUCH_VALUE]: 'パウチの数値を編集',
    [HistoryActionType.EDIT_GTP_VALUE]: 'GTP の数値を編集',
    [HistoryActionType.EDIT_POUCH_TITLE]: 'パウチ名を編集',
    [HistoryActionType.EDIT_GROUP_TITLE]: 'グループ名を編集',
    [HistoryActionType.MOVE_POUCH]: 'パウチを移動',
    [HistoryActionType.DELETE_POUCH]: 'パウチを削除',
    [HistoryActionType.ADD_GROUP]: 'グループを追加',
    [HistoryActionType.MOVE_GROUP]: 'グループを移動',
    [HistoryActionType.RESIZE_GROUP]: 'グループをリサイズ',
    [HistoryActionType.DELETE_GROUP]: 'グループを削除',
    [HistoryActionType.CHANGE_GROUP_COLOR]: 'グループの色を変更',
    [HistoryActionType.MERGE_GROUP]: 'グループをマージ',
    [HistoryActionType.MERGE_GTP]: 'グループ合計パウチをマージ',
    [HistoryActionType.CHANGE_POUCH_COLOR]: 'パウチの色を変更',
    [HistoryActionType.RESTORE_SNAPSHOT]: 'スナップショットを復元',
    [HistoryActionType.CLONE_POUCH]: 'パウチを複製',
    [HistoryActionType.CLONE_GROUP]: 'グループを複製',
    [HistoryActionType.MERGE_POUCH]: 'パウチをマージ',
    [HistoryActionType.SPLIT_POUCH]: 'パウチを分解',
    [HistoryActionType.PASTE_BULK_UPDATE]: '一括ペースト更新',
    [HistoryActionType.TOGGLE_BALANCING_POUCH]: '帳尻パウチを切り替え',
    [HistoryActionType.EDIT_POUCH_LAYER_VALUE]: 'レイヤー数値を編集',
    [HistoryActionType.ADD_SHEET_LAYER]: 'レイヤーを追加',
    [HistoryActionType.REMOVE_SHEET_LAYER]: 'レイヤーを削除',
    [HistoryActionType.RENAME_SHEET_LAYER]: 'レイヤー名を編集',
  },
  zh: {
    [HistoryActionType.INIT]: '初始状态',
    [HistoryActionType.ADD_POUCH]: '添加小袋',
    [HistoryActionType.EDIT_POUCH_VALUE]: '编辑小袋数值',
    [HistoryActionType.EDIT_GTP_VALUE]: '编辑 GTP 数值',
    [HistoryActionType.EDIT_POUCH_TITLE]: '编辑袋名',
    [HistoryActionType.EDIT_GROUP_TITLE]: '编辑组名',
    [HistoryActionType.MOVE_POUCH]: '移动小袋',
    [HistoryActionType.DELETE_POUCH]: '删除小袋',
    [HistoryActionType.ADD_GROUP]: '添加分组',
    [HistoryActionType.MOVE_GROUP]: '移动分组',
    [HistoryActionType.RESIZE_GROUP]: '调整分组大小',
    [HistoryActionType.DELETE_GROUP]: '删除分组',
    [HistoryActionType.CHANGE_GROUP_COLOR]: '更改分组颜色',
    [HistoryActionType.MERGE_GROUP]: '合并分组',
    [HistoryActionType.MERGE_GTP]: '合并分组合计小袋',
    [HistoryActionType.CHANGE_POUCH_COLOR]: '更改小袋颜色',
    [HistoryActionType.RESTORE_SNAPSHOT]: '恢复快照',
    [HistoryActionType.CLONE_POUCH]: '复制小袋',
    [HistoryActionType.CLONE_GROUP]: '复制分组',
    [HistoryActionType.MERGE_POUCH]: '合并小袋',
    [HistoryActionType.SPLIT_POUCH]: '分解小袋',
    [HistoryActionType.PASTE_BULK_UPDATE]: '批量粘贴更新',
    [HistoryActionType.TOGGLE_BALANCING_POUCH]: '切换平衡小袋',
    [HistoryActionType.EDIT_POUCH_LAYER_VALUE]: '编辑图层数值',
    [HistoryActionType.ADD_SHEET_LAYER]: '添加图层',
    [HistoryActionType.REMOVE_SHEET_LAYER]: '删除图层',
    [HistoryActionType.RENAME_SHEET_LAYER]: '编辑图层名称',
  },
};

const HISTORY_COPY: Record<AppLocale, HistoryCopy> = {
  en: {
    snapshot: {
      defaultName: (n) => `Snapshot ${n}`,
      createdAt: (datetime) => `Created at ${datetime}`,
      loadAction: 'Load Snapshot',
    },
    dynamic: {
      allClear: 'All Clear',
      moveGroupTo: (sheet) => `Move Group to ${sheet}`,
      movePouchTo: (sheet) => `Move Pouch to ${sheet}`,
      moveGroupFrom: (sheet) => `Move Group from ${sheet}`,
      movePouchFrom: (sheet) => `Move Pouch from ${sheet}`,
      clonePouchFrom: (sheet) => `Clone Pouch from ${sheet}`,
      cloneGroupFrom: (sheet) => `Clone Group from ${sheet}`,
      enableBalancing: 'Enable Balancing Pouch',
      disableBalancing: 'Disable Balancing Pouch',
      changeBalancing: 'Change Balancing Pouch',
      welcomeContent: 'Welcome content',
    },
  },
  ja: {
    snapshot: {
      defaultName: (n) => `スナップショット ${n}`,
      createdAt: (datetime) => `${datetime} に作成`,
      loadAction: 'スナップショットを読み込み',
    },
    dynamic: {
      allClear: 'すべてクリア',
      moveGroupTo: (sheet) => `グループを ${sheet} に移動`,
      movePouchTo: (sheet) => `パウチを ${sheet} に移動`,
      moveGroupFrom: (sheet) => `グループを ${sheet} から移動`,
      movePouchFrom: (sheet) => `パウチを ${sheet} から移動`,
      clonePouchFrom: (sheet) => `パウチを ${sheet} から複製`,
      cloneGroupFrom: (sheet) => `グループを ${sheet} から複製`,
      enableBalancing: '帳尻パウチを有効化',
      disableBalancing: '帳尻パウチを無効化',
      changeBalancing: '帳尻パウチを変更',
      welcomeContent: 'ウェルカムコンテンツ',
    },
  },
  zh: {
    snapshot: {
      defaultName: (n) => `快照 ${n}`,
      createdAt: (datetime) => `创建于 ${datetime}`,
      loadAction: '加载快照',
    },
    dynamic: {
      allClear: '全部清除',
      moveGroupTo: (sheet) => `将分组移至 ${sheet}`,
      movePouchTo: (sheet) => `将小袋移至 ${sheet}`,
      moveGroupFrom: (sheet) => `将分组从 ${sheet} 移出`,
      movePouchFrom: (sheet) => `将小袋从 ${sheet} 移出`,
      clonePouchFrom: (sheet) => `从 ${sheet} 复制小袋`,
      cloneGroupFrom: (sheet) => `从 ${sheet} 复制分组`,
      enableBalancing: '启用平衡小袋',
      disableBalancing: '禁用平衡小袋',
      changeBalancing: '更改平衡小袋',
      welcomeContent: '欢迎内容',
    },
  },
};

export const HISTORY_STORAGE_LOCALE: AppLocale = 'en';

export function historyActionLabel(type: HistoryActionType, locale: AppLocale): string {
  return ACTION_LABELS[locale][type] ?? ACTION_LABELS.en[type] ?? type;
}

export function getHistoryCopy(locale: AppLocale): HistoryCopy {
  return HISTORY_COPY[locale];
}

export function getHistoryStorageCopy(): HistoryCopy {
  return HISTORY_COPY[HISTORY_STORAGE_LOCALE];
}

const LOCALES: AppLocale[] = ['en', 'ja', 'zh'];

function staticDynamicMatches(
  description: string,
  key: keyof HistoryCopy['dynamic']
): boolean {
  return LOCALES.some((loc) => HISTORY_COPY[loc].dynamic[key] === description);
}

function descriptionMatchesActionType(description: string, type: HistoryActionType): boolean {
  if (description === type) return true;
  return LOCALES.some((loc) => ACTION_LABELS[loc][type] === description);
}

function inferActionTypeFromDescription(description: string): HistoryActionType | null {
  for (const type of Object.values(HistoryActionType)) {
    if (descriptionMatchesActionType(description, type)) return type;
  }
  return null;
}

function extractSheetName(description: string, patterns: RegExp[]): string | null {
  for (const pattern of patterns) {
    const match = description.match(pattern);
    if (match) return match[1];
  }
  return null;
}

const MOVE_GROUP_TO_PATTERNS = [
  /^Move Group to (.+)$/,
  /^グループを (.+) に移動$/,
  /^将分组移至 (.+)$/,
];
const MOVE_POUCH_TO_PATTERNS = [
  /^Move Pouch to (.+)$/,
  /^パウチを (.+) に移動$/,
  /^将小袋移至 (.+)$/,
];
const MOVE_GROUP_FROM_PATTERNS = [
  /^Move Group from (.+)$/,
  /^グループを (.+) から移動$/,
  /^将分组从 (.+) 移出$/,
];
const MOVE_POUCH_FROM_PATTERNS = [
  /^Move Pouch from (.+)$/,
  /^パウチを (.+) から移動$/,
  /^将小袋从 (.+) 移出$/,
];
const CLONE_POUCH_FROM_PATTERNS = [
  /^Clone Pouch from (.+)$/,
  /^パウチを (.+) から複製$/,
  /^从 (.+) 复制小袋$/,
];
const CLONE_GROUP_FROM_PATTERNS = [
  /^Clone Group from (.+)$/,
  /^グループを (.+) から複製$/,
  /^从 (.+) 复制分组$/,
];

/** 保存済み description（英語正本＋旧ロケール文字列）を表示用に変換 */
export function localizeHistoryDescription(
  description: string,
  type: HistoryActionType | undefined,
  locale: AppLocale
): string {
  const copy = getHistoryCopy(locale);

  if (staticDynamicMatches(description, 'allClear')) return copy.dynamic.allClear;
  if (staticDynamicMatches(description, 'enableBalancing')) return copy.dynamic.enableBalancing;
  if (staticDynamicMatches(description, 'disableBalancing')) return copy.dynamic.disableBalancing;
  if (staticDynamicMatches(description, 'changeBalancing')) return copy.dynamic.changeBalancing;
  if (staticDynamicMatches(description, 'welcomeContent')) return copy.dynamic.welcomeContent;

  if (
    LOCALES.some((loc) => HISTORY_COPY[loc].snapshot.loadAction === description) ||
    description === 'スナップショットを読み込む'
  ) {
    return copy.snapshot.loadAction;
  }

  const moveGroupTo = extractSheetName(description, MOVE_GROUP_TO_PATTERNS);
  if (moveGroupTo !== null) return copy.dynamic.moveGroupTo(moveGroupTo);

  const movePouchTo = extractSheetName(description, MOVE_POUCH_TO_PATTERNS);
  if (movePouchTo !== null) return copy.dynamic.movePouchTo(movePouchTo);

  const moveGroupFrom = extractSheetName(description, MOVE_GROUP_FROM_PATTERNS);
  if (moveGroupFrom !== null) return copy.dynamic.moveGroupFrom(moveGroupFrom);

  const movePouchFrom = extractSheetName(description, MOVE_POUCH_FROM_PATTERNS);
  if (movePouchFrom !== null) return copy.dynamic.movePouchFrom(movePouchFrom);

  const clonePouchFrom = extractSheetName(description, CLONE_POUCH_FROM_PATTERNS);
  if (clonePouchFrom !== null) return copy.dynamic.clonePouchFrom(clonePouchFrom);

  const cloneGroupFrom = extractSheetName(description, CLONE_GROUP_FROM_PATTERNS);
  if (cloneGroupFrom !== null) return copy.dynamic.cloneGroupFrom(cloneGroupFrom);

  if (type && descriptionMatchesActionType(description, type)) {
    return historyActionLabel(type, locale);
  }

  const inferred = inferActionTypeFromDescription(description);
  if (inferred) return historyActionLabel(inferred, locale);

  return description;
}
